import { useIsRibbonRail } from '@/app-shell/layout/Navigation/hooks/useIsRibbonRail';
import { useIsRouteActive } from '@/app-shell/layout/Navigation/hooks/useIsRouteActive';
import { useMobilePanel } from '@/app-shell/layout/Navigation/mobile';
import {
  useFooterRailCollapsible,
  type FooterMoreRowDescriptor,
} from '@/app-shell/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import { NavItem } from '@/app-shell/layout/Navigation/sidebar/nav-item';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { useIntegrationsMarketplaceVisibilityGate } from '@/app-shell/settings/integrations/integrations-marketplace-visibility-gate';
import { SidebarMenu } from '@heidi/design-system/page-shell';
import { useIsIntegrationButtonInSidebarEnabled } from '@heidi/integrations/flags';
import { Plug } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { integrationsNavItemMessages } from './IntegrationsNavItem.messages';

export const IntegrationsNavItem = () => {
  const isEnabled = useIsIntegrationButtonInSidebarEnabled();
  const marketplaceVisibility = useIntegrationsMarketplaceVisibilityGate();
  const intl = useIntl();
  const { dismissMobileSidebar } = useMobilePanel();
  const isRouteActive = useIsRouteActive();
  const { open: openSettingsModal, state: settingsModalState } = useSettingsModal();
  const isRibbonRail = useIsRibbonRail();
  const isMarketplaceVisible = marketplaceVisibility === 'visible';

  // Ungated on purpose. Marketplace and settings copy still follow
  // release-integrations-becomes-connectors via useIntegrationsVerbiage.
  // This rail has its own message so the hover label is Connectors for every cohort.
  const label = intl.formatMessage(integrationsNavItemMessages.label);

  const onClick = useCallback(() => {
    openSettingsModal('/settings/integrations', 'integrations_sidebar_button');
    dismissMobileSidebar();
  }, [openSettingsModal, dismissMobileSidebar]);

  // The modal never puts `/settings/integrations` in the URL (it uses
  // `?settings=integrations`), so `isRouteActive` alone can't detect the
  // in-modal case — read the modal's own active section, the same source
  // `SettingsSideBar` uses. The router check still covers the real
  // standalone `/settings/integrations/<slug>` detail pages.
  const isActive =
    (settingsModalState.open && settingsModalState.route === '/settings/integrations') ||
    isRouteActive('/settings/integrations');

  const descriptor = useMemo<FooterMoreRowDescriptor>(
    () => ({
      id: 'integrations',
      label,
      icon: <Plug />,
      testId: 'sidebar-link-integrations',
      onSelect: onClick,
      isActive,
    }),
    [label, onClick, isActive]
  );
  const isCollapsed = useFooterRailCollapsible({
    id: 'integrations',
    visible: Boolean(isEnabled) && isMarketplaceVisible && isRibbonRail,
    pinned: false,
    descriptor,
  });

  if (!isEnabled || !isMarketplaceVisible || isCollapsed) {
    return null;
  }

  // Rendered via `NavItem` rather than a bare `SidebarMenuButton` so the
  // central offline gating applies — the settings page behind this item is
  // backend-backed and must be disabled while offline. `testId` is deliberately
  // absent from `OFFLINE_AVAILABLE_NAV_TEST_IDS`.
  const item = (
    <NavItem
      label={label}
      icon={<Plug />}
      onClick={onClick}
      isActive={isActive}
      layout={isRibbonRail ? 'iconOnly' : 'default'}
      testId="sidebar-link-integrations"
    />
  );

  // Ribbon rail: bare item — the shared footer <ul> is owned by SideNavFooter.
  return isRibbonRail ? item : <SidebarMenu>{item}</SidebarMenu>;
};
